import * as React from 'react';

import * as Theme from '@sail/theme-classic';
import {view} from '../view';
import {css} from '../css';

import {formFieldGenerator} from './FormField';
import {useFieldContexts} from '../../components/hooks/useFieldContexts';

export type NativeTextAreaProps = Pick<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  | 'autoComplete'
  | 'autoFocus'
  | 'cols'
  | 'defaultValue'
  | 'disabled'
  | 'form'
  | 'inputMode'
  | 'maxLength'
  | 'minLength'
  | 'name'
  | 'onChange'
  | 'onKeyPress'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'placeholder'
  | 'readOnly'
  | 'required'
  | 'spellCheck'
  | 'tabIndex'
  | 'value'
  | 'wrap'
> & {
  /**
   * Programmatically mark the value as invalid
   * @external
   */
  invalid?: boolean;
  /**
   * The size of the input
   * @external
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Allow the user to resize the textarea
   * @external
   */
  resizeable?: boolean;
  /**
   * Rows of text that are visible in the input
   * @external
   */
  rows?: number;
};

const disabledStyles = css({
  '&:disabled': {
    overflow: 'hidden',
  },
});

export const NativeTextArea = view.textarea.create<NativeTextAreaProps>(
  ({resizeable = true, size = 'medium', rows = 3, ...props}, View) => {
    // if these are part of props, pull them off so they don't override the values from context
    const {invalid: _, disabled: __, ...restProps} = props;

    const {id, invalid, ariaDescribedBy, ariaLabelledBy, disabled} =
      useFieldContexts(props);

    return (
      <View
        id={id}
        disabled={disabled}
        rows={rows}
        aria-invalid={invalid}
        aria-describedby={ariaDescribedBy}
        aria-labelledby={ariaLabelledBy}
        uses={[
          disabledStyles,
          restProps,
          Theme.TextFieldVariants.assign({
            invalid,
            size,
          }),
          Theme.TextAreaVariants.assign({
            invalid,
            resizeable,
            size,
          }),
          Theme.focus,
          Theme.TextField,
          Theme.TextArea,
        ]}
      />
    );
  },
  [Theme.TextAreaVariants.internal_defaults],
);

/**
 * @external
 * @param autoComplete
 * @param autoFocus
 * @param cols
 * @param defaultValue
 * @param disabled
 * @param form
 * @param maxLength
 * @param minLength
 * @param name
 * @param onChange
 * @param onKeyPress
 * @param onKeyDown
 * @param onKeyUp
 * @param placeholder
 * @param readOnly
 * @param required
 * @param spellCheck
 * @param tabIndex
 * @param wrap
 */
export const TextArea = formFieldGenerator<NativeTextAreaProps>(NativeTextArea);
